import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { API_URL, DISPATCH_BOARD_URL } from "../../config";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import ChargeConfirmModal from "./ChargeConfirmModal";
import CouldNotBookModal from "./CouldNotBookModal";
import RateConUploadModal from "./RateConUploadModal";
import ParadeTransactModal from "./ParadeTransactModal";
import {
  authHeaders,
  feeByLoadAmount,
  feeCellText,
  fetchFeeTiersText,
} from "./feeTiers";

function Spinner({ size = 16, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={`animate-spin ${className}`}
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        opacity="0.25"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IndeterminateBar() {
  // Top-of-table sliding bar for refetch-while-data-present
  return (
    <div className="h-0.5 w-full bg-indigo-100 overflow-hidden">
      <div className="h-full w-1/3 bg-indigo-500 animate-[indeterminateSlide_1.2s_ease-in-out_infinite]" />
      <style>{`
        @keyframes indeterminateSlide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}

function TruckerSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Close on outside click
  useEffect(() => {
    function onClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Reset filter text when value changes externally
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((name) => name.toLowerCase().includes(q));
  }, [query, options]);

  function pick(name) {
    onChange(name);
    setQuery(name);
    setOpen(false);
    setActiveIndex(-1);
  }

  function onKey(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && filtered[activeIndex]) {
        e.preventDefault();
        pick(filtered[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative w-56">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="All truckers"
        autoComplete="off"
        style={{ colorScheme: "light" }}
        className="w-full bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md pl-3 pr-7 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      {(query || value) && (
        <button
          type="button"
          onClick={() => {
            onChange("");
            setQuery("");
            setOpen(false);
          }}
          aria-label="Clear trucker"
          className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !bg-transparent !border-0 !shadow-none !text-slate-400 hover:!text-slate-700 !px-1.5 !py-0 !text-base !leading-none"
        >
          ×
        </button>
      )}
      {open && (
        <div
          ref={listRef}
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
          ) : (
            filtered.map((name, idx) => (
              <button
                key={name}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(name);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`!shadow-none !border-0 !rounded-none w-full !text-left !px-3 !py-1.5 !text-sm !bg-transparent ${
                  idx === activeIndex
                    ? "!bg-indigo-50 !text-indigo-900"
                    : "!text-slate-800 hover:!bg-slate-100"
                }`}
              >
                {name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Status presets for the "All" tab — keys map to filter functions over a booking_request row.
const ALL_PRESETS = {
  active: (r) => ["suggested", "link_opened"].includes(r.status),
  booked: (r) =>
    r.status === "booked" && r.payment_status !== "failed_post_booking",
  collections: (r) => r.payment_status === "failed_post_booking",
  other: (r) =>
    ["failed", "expired", "declined"].includes(r.status) &&
    r.payment_status !== "failed_post_booking",
  all: () => true,
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("pending"); // "pending" | "all"
  const [allFilter, setAllFilter] = useState("active");
  const [pending, setPending] = useState([]);
  const [sent, setSent] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chargeTarget, setChargeTarget] = useState(null);
  const [failTarget, setFailTarget] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  // { row, mode: "quote" | "book" } | null
  const [paradeTarget, setParadeTarget] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [feeTiersText, setFeeTiersText] = useState(null);

  // Filters apply to every tab
  const [filterTrucker, setFilterTrucker] = useState("");
  const [filterSince, setFilterSince] = useState(""); // YYYY-MM-DD
  const [filterUntil, setFilterUntil] = useState(""); // YYYY-MM-DD
  const [truckerOptions, setTruckerOptions] = useState([]);
  const [pendingChrQueue, setPendingChrQueue] = useState("all");
  const [pendingQueueCounts, setPendingQueueCounts] = useState({
    ready_to_close: 0,
    waiting_chr: 0,
    reserved_only: 0,
    needs_intervention: 0,
  });

  // Fetch ALL data — no filter params. Filtering happens client-side from
  // the in-memory arrays so UI feels instant.
  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [p, s, h] = await Promise.all([
        axios.get(`${API_URL}/book-it/admin/pending`, {
          headers: authHeaders(),
        }),
        axios.get(`${API_URL}/book-it/admin/sent`, { headers: authHeaders() }),
        axios.get(`${API_URL}/book-it/admin/history`, {
          headers: authHeaders(),
        }),
      ]);
      setPending(p.data.items || []);
      setPendingQueueCounts(
        p.data.queue_counts || {
          ready_to_close: 0,
          waiting_chr: 0,
          reserved_only: 0,
          needs_intervention: 0,
        },
      );
      setSent(s.data.items || []);
      setHistory(h.data.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load admin data.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Retried on the 60s refresh until it loads, so one failed request doesn't
  // leave the copy without amounts until a reload.
  useEffect(() => {
    if (feeTiersText) return undefined;
    let cancelled = false;
    const load = () =>
      fetchFeeTiersText().then((text) => {
        if (!cancelled && text) setFeeTiersText(text);
      });
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [feeTiersText]);

  // Background refresh every 60s — silent so the UI doesn't flash loading state
  useEffect(() => {
    const id = setInterval(() => fetchAll({ silent: true }), 60_000);
    return () => clearInterval(id);
  }, [fetchAll]);

  // Populate trucker dropdown once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/truckers/`, {
          headers: authHeaders(),
          params: { limit: 100, sort: "name", dir: "asc" },
        });
        if (!cancelled) {
          const names = (data.truckers || [])
            .map((t) => t.name)
            .filter(Boolean)
            .sort();
          setTruckerOptions(names);
        }
      } catch {
        // silent — dropdown stays empty, filter still works as free text
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(() => fetchAll(), [fetchAll]);

  async function retryChrSubmit(row) {
    const id = row.booking_request_id;
    setBusyAction(`retry:${id}`);
    setError("");
    try {
      await axios.post(
        `${API_URL}/book-it/admin/${id}/retry-chr-submit`,
        {},
        { headers: authHeaders() },
      );
      await fetchAll({ silent: true });
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Could not retry the broker request.",
      );
    } finally {
      setBusyAction(null);
    }
  }

  async function resetBookItLink(row) {
    const id = row.booking_request_id;
    setBusyAction(`reset:${id}`);
    setError("");
    try {
      const { data } = await axios.post(
        `${API_URL}/book-it/admin/${id}/reset-book-it-link`,
        {},
        { headers: authHeaders() },
      );
      if (data?.link && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(data.link);
      }
      await fetchAll({ silent: true });
      if (data?.link) {
        window.prompt(
          "New Book It link (copied if clipboard allowed):",
          data.link,
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Could not reset the Book It link.",
      );
    } finally {
      setBusyAction(null);
    }
  }

  function clearFilters() {
    setFilterTrucker("");
    setFilterSince("");
    setFilterUntil("");
  }
  const hasActiveFilters = !!(
    filterTrucker.trim() ||
    filterSince ||
    filterUntil
  );

  async function viewRateCon(row, fileIndex) {
    try {
      const { data } = await axios.get(
        `${API_URL}/book-it/admin/${row.booking_request_id}/rate-confirmation/${fileIndex}/url`,
        { headers: authHeaders() },
      );
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Could not open rate confirmation.",
      );
    }
  }

  // Client-side filter. `dateField` can be a string key or a function (row) => isoString.
  const applyFilters = useCallback(
    (items, dateField) => {
      const tName = filterTrucker.trim().toLowerCase();
      const sinceMs = filterSince
        ? new Date(`${filterSince}T00:00:00`).getTime()
        : null;
      const untilMs = filterUntil
        ? new Date(`${filterUntil}T23:59:59`).getTime()
        : null;
      if (!tName && sinceMs == null && untilMs == null) return items;

      return items.filter((r) => {
        if (tName && (r.trucker_name || "").toLowerCase() !== tName)
          return false;
        if (sinceMs != null || untilMs != null) {
          const raw =
            typeof dateField === "function" ? dateField(r) : r[dateField];
          if (!raw) return false;
          const t = new Date(raw).getTime();
          if (Number.isNaN(t)) return false;
          if (sinceMs != null && t < sinceMs) return false;
          if (untilMs != null && t > untilMs) return false;
        }
        return true;
      });
    },
    [filterTrucker, filterSince, filterUntil],
  );

  // Merged Sent + History sorted newest-first by resolved_at (terminal rows) or
  // created_at (in-flight rows). This is the data source for the "All" tab.
  const merged = useMemo(() => {
    return [...sent, ...history].sort((a, b) => {
      const da = new Date(a.resolved_at || a.created_at || 0).getTime();
      const db = new Date(b.resolved_at || b.created_at || 0).getTime();
      return db - da;
    });
  }, [sent, history]);

  const filteredPending = useMemo(() => {
    let items = applyFilters(pending, "saved_at");
    if (pendingChrQueue === "ready") {
      items = items.filter((r) => r.chr_ops_attention === "ready_to_close");
    } else if (pendingChrQueue === "waiting") {
      items = items.filter((r) => r.chr_ops_attention === "waiting_chr");
    } else if (pendingChrQueue === "needs") {
      items = items.filter((r) => r.chr_ops_attention === "needs_intervention");
    }
    return items;
  }, [pending, applyFilters, pendingChrQueue]);
  const filteredAll = useMemo(
    () => applyFilters(merged, (r) => r.resolved_at || r.created_at),
    [merged, applyFilters],
  );

  // Counts shown on the preset chips — derived from the trucker/date-filtered
  // "All" rows so chip counts respect the global filters.
  const presetCounts = useMemo(() => {
    const c = {};
    for (const key of Object.keys(ALL_PRESETS)) {
      c[key] = filteredAll.filter(ALL_PRESETS[key]).length;
    }
    return c;
  }, [filteredAll]);

  const rows =
    tab === "pending"
      ? filteredPending
      : filteredAll.filter(ALL_PRESETS[allFilter]);

  return (
    <DashboardLayout contentBackground="#f6f7fb">
      <div className="w-full max-w-6xl mx-auto px-6 py-8 md:px-8 md:py-10 text-slate-900 antialiased">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            Carrier Payments
          </h1>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="!bg-white !text-slate-700 hover:!text-indigo-600 hover:!border-indigo-500 !border !border-slate-300 !rounded-lg !px-3 !py-2 !text-sm !font-medium !shadow-none disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <span aria-hidden>↻</span>
            <span>{loading ? "Refreshing…" : "Refresh"}</span>
          </button>
        </div>
        <p className="mt-1 mb-7 text-sm text-slate-500">
          Upload the rate confirmation and enter the negotiated rate. The flat
          fee is set {feeByLoadAmount(feeTiersText)} and calculated
          automatically — you can charge once both are present.
        </p>

        <div className="inline-flex bg-slate-200 rounded-lg p-1 mb-5 gap-0.5">
          <button
            type="button"
            onClick={() => setTab("pending")}
            className={`!shadow-none !border-0 !rounded-md !px-4 !py-1.5 !text-sm !font-semibold inline-flex items-center gap-2 transition-colors ${
              tab === "pending"
                ? "!bg-white !text-slate-900"
                : "!bg-transparent !text-slate-500 hover:!text-slate-700"
            }`}
          >
            Pending
            {filteredPending.length > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full text-[11px] font-bold px-2 leading-none ${
                  tab === "pending"
                    ? "bg-indigo-600 text-white"
                    : "bg-slate-300 text-slate-600"
                }`}
              >
                {filteredPending.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab("all")}
            className={`!shadow-none !border-0 !rounded-md !px-4 !py-1.5 !text-sm !font-semibold inline-flex items-center gap-2 transition-colors ${
              tab === "all"
                ? "!bg-white !text-slate-900"
                : "!bg-transparent !text-slate-500 hover:!text-slate-700"
            }`}
          >
            All
            {filteredAll.length > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full text-[11px] font-bold px-2 leading-none ${
                  tab === "all"
                    ? "bg-slate-700 text-white"
                    : "bg-slate-300 text-slate-600"
                }`}
              >
                {filteredAll.length}
              </span>
            )}
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-5 p-3 bg-white border border-slate-200 rounded-lg">
          <div className="flex flex-col">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Trucker
            </label>
            <TruckerSelect
              value={filterTrucker}
              onChange={setFilterTrucker}
              options={truckerOptions}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              From
            </label>
            <input
              type="date"
              value={filterSince}
              onChange={(e) => setFilterSince(e.target.value)}
              style={{ colorScheme: "light" }}
              className="bg-white text-slate-900 border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              To
            </label>
            <input
              type="date"
              value={filterUntil}
              onChange={(e) => setFilterUntil(e.target.value)}
              style={{ colorScheme: "light" }}
              className="bg-white text-slate-900 border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {tab === "pending" && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mr-1">
                CHR queue
              </span>
              {[
                { id: "all", label: "All pending" },
                {
                  id: "ready",
                  label: `Ready to close (${pendingQueueCounts.ready_to_close})`,
                },
                {
                  id: "waiting",
                  label: `Waiting on CHR (${pendingQueueCounts.waiting_chr})`,
                },
                {
                  id: "needs",
                  label: `Needs intervention (${pendingQueueCounts.needs_intervention || 0})`,
                },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPendingChrQueue(id)}
                  className={`!shadow-none !border !rounded-md !px-3 !py-1 !text-xs !font-semibold ${
                    pendingChrQueue === id
                      ? "!bg-indigo-600 !text-white !border-indigo-600"
                      : "!bg-white !text-slate-600 !border-slate-300 hover:!border-indigo-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {tab === "all" && (
            <div className="flex flex-col">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Status
              </label>
              <select
                value={allFilter}
                onChange={(e) => setAllFilter(e.target.value)}
                className="bg-white text-slate-900 border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="active">Active ({presetCounts.active})</option>
                <option value="booked">Booked ({presetCounts.booked})</option>
                <option value="collections">
                  Needs collection ({presetCounts.collections})
                </option>
                <option value="other">
                  Failed / Expired / Declined ({presetCounts.other})
                </option>
                <option value="all">All ({presetCounts.all})</option>
              </select>
            </div>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="!bg-transparent !text-slate-500 hover:!text-slate-700 !border-0 !px-2 !py-2 !text-sm !shadow-none"
            >
              Clear filters
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
            {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl py-16 px-6 text-center">
            <div className="inline-flex items-center gap-3 text-slate-500 text-sm">
              <Spinner size={20} className="text-indigo-500" />
              <span>Loading carrier payments…</span>
            </div>
          </div>
        ) : !loading && rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl py-16 px-6 text-center">
            <div className="text-3xl mb-3 opacity-70">
              {tab === "pending"
                ? "✨"
                : allFilter === "active"
                  ? "📤"
                  : allFilter === "booked"
                    ? "✅"
                    : allFilter === "collections"
                      ? "💸"
                      : "📭"}
            </div>
            <p className="text-base font-semibold text-slate-700 mb-1">
              {tab === "pending"
                ? "Nothing to charge yet"
                : allFilter === "active"
                  ? "No links in flight"
                  : allFilter === "booked"
                    ? "No booked loads yet"
                    : allFilter === "collections"
                      ? "No failed charges to collect"
                      : allFilter === "other"
                        ? "No failed, expired, or declined loads"
                        : "No booking requests yet"}
            </p>
            <p className="text-sm text-slate-400">
              {tab === "pending"
                ? "Carriers who authorize the reservation fee will show up here."
                : allFilter === "active"
                  ? "Links you've sent but where the carrier hasn't added a payment method yet will appear here until they save one or the link expires."
                  : allFilter === "booked"
                    ? "Once you charge and book a carrier, the request lands here."
                    : allFilter === "collections"
                      ? "When an ACH bounces after we've already booked, it lands here for follow-up."
                      : allFilter === "other"
                        ? "Links that didn't convert will appear here."
                        : "Once carriers receive Book-It links, the requests will appear here."}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden relative">
            {loading && <IndeterminateBar />}
            <div
              className={`overflow-x-auto transition-opacity ${loading ? "opacity-60" : ""}`}
            >
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Trucker
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      When
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Lane
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Load
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Pickup
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Posted
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Negotiated
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Rate Con
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      {tab === "pending" ? "Fee" : "Charged"}
                    </th>
                    <th className="text-right px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const lane = formatLane(row.load_snapshot);
                    const posted = formatRateCents(
                      row.posted_rate_cents,
                      "TBD",
                    );
                    const negotiated = formatRateCents(
                      row.negotiated_rate_cents,
                    );
                    const method = formatMethod(row);
                    const rateConCount = (row.rate_confirmation_files || [])
                      .length;
                    const hasRateCon = rateConCount > 0;
                    const hasNegotiated = !!row.negotiated_rate_cents;
                    const canCharge = hasRateCon && hasNegotiated;

                    const feeDisplay = feeCellText(row);
                    const freeCredit = row.fee_preview_cents === 0;

                    const isFailedPostBooking =
                      row.payment_status === "failed_post_booking";
                    const resolutionNote =
                      row.status === "declined"
                        ? row.decline_reason
                        : row.resolution_note;

                    const statusLabel = isFailedPostBooking
                      ? "Charge bounced"
                      : row.status === "payment_method_saved"
                        ? "Awaiting"
                        : row.status === "suggested"
                          ? "Link sent"
                          : row.status === "link_opened"
                            ? "Opened"
                            : row.status === "booked"
                              ? "Booked"
                              : row.status === "failed"
                                ? "Could not book"
                                : row.status === "expired"
                                  ? "Expired"
                                  : row.status === "declined"
                                    ? "Declined"
                                    : row.status;
                    const statusClass = isFailedPostBooking
                      ? "bg-rose-50 text-rose-700"
                      : row.status === "payment_method_saved"
                        ? "bg-indigo-50 text-indigo-700"
                        : row.status === "suggested"
                          ? "bg-slate-100 text-slate-600"
                          : row.status === "link_opened"
                            ? "bg-sky-50 text-sky-700"
                            : row.status === "booked"
                              ? "bg-emerald-50 text-emerald-700"
                              : row.status === "failed"
                                ? "bg-amber-50 text-amber-800"
                                : "bg-slate-100 text-slate-600";

                    return (
                      <tr
                        key={row.booking_request_id}
                        className={`border-b border-slate-100 last:border-b-0 align-top ${
                          isFailedPostBooking
                            ? "bg-rose-50/40 hover:bg-rose-50"
                            : row.chr_ops_attention === "ready_to_close"
                              ? "bg-emerald-50/50 hover:bg-emerald-50"
                              : row.chr_ops_attention === "needs_intervention"
                                ? "bg-rose-50/30 hover:bg-rose-50"
                                : "hover:bg-slate-50"
                        }`}
                      >
                        <td className="px-4 py-4 text-sm">
                          <div className="font-semibold text-slate-900">
                            {row.trucker_name || "—"}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {row.trucker_phone || ""}
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${statusClass}`}
                            >
                              {statusLabel}
                              {!isFailedPostBooking &&
                              row.status === "booked" &&
                              row.payment_status
                                ? ` · ${row.payment_status}`
                                : ""}
                            </span>
                            <span className="text-[11px] text-slate-500 whitespace-nowrap">
                              {method}
                            </span>
                            {row.chr_ops_attention === "ready_to_close" && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap bg-emerald-100 text-emerald-800 border border-emerald-200">
                                CHR confirmed
                              </span>
                            )}
                            {row.chr_ops_attention === "waiting_chr" && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap bg-amber-50 text-amber-900 border border-amber-200"
                                title={row.chr_bidding_status || ""}
                              >
                                CHR:{" "}
                                {(
                                  row.chr_bidding_status || "in progress"
                                ).replace(/_/g, " ")}
                              </span>
                            )}
                            {row.chr_ops_attention === "needs_intervention" && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap bg-rose-100 text-rose-800 border border-rose-200"
                                title={
                                  row.chr_recommended_action ||
                                  row.chr_last_error ||
                                  row.chr_bidding_status ||
                                  ""
                                }
                              >
                                Manual booking
                                {row.chr_last_http_status
                                  ? ` · ${row.chr_last_http_status}`
                                  : ""}
                              </span>
                            )}
                          </div>
                          {resolutionNote && (
                            <div
                              className="text-[11px] text-slate-500 mt-1.5 italic max-w-[180px] truncate"
                              title={resolutionNote}
                            >
                              "{resolutionNote}"
                            </div>
                          )}
                          {row.negotiation_amount_cents != null && (
                            <div
                              className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200 whitespace-nowrap"
                              title={
                                row.negotiation_notes
                                  ? `Notes: ${row.negotiation_notes}`
                                  : "Counter-offer from trucker"
                              }
                            >
                              💬 $
                              {(
                                row.negotiation_amount_cents / 100
                              ).toLocaleString()}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-800 whitespace-nowrap align-top">
                          {(() => {
                            const STAGE_STYLES = {
                              Suggested: {
                                icon: "✉",
                                dot: "bg-slate-400",
                                text: "text-slate-700",
                              },
                              Opened: {
                                icon: "👁",
                                dot: "bg-sky-500",
                                text: "text-sky-700",
                              },
                              Counter: {
                                icon: "💬",
                                dot: "bg-amber-500",
                                text: "text-amber-800",
                              },
                              Authorized: {
                                icon: "🔒",
                                dot: "bg-indigo-500",
                                text: "text-indigo-700",
                              },
                              Resolved: {
                                icon: "✓",
                                dot: "bg-emerald-500",
                                text: "text-emerald-700",
                              },
                              Settled: {
                                icon: "🏦",
                                dot: "bg-emerald-600",
                                text: "text-emerald-700",
                              },
                            };
                            const events = [
                              {
                                label: "Suggested",
                                ts: row.created_at || row.suggested_at,
                              },
                              { label: "Opened", ts: row.opened_at },
                              {
                                label: "Counter",
                                ts: row.negotiation_submitted_at,
                              },
                              { label: "Authorized", ts: row.saved_at },
                              { label: "Resolved", ts: row.resolved_at },
                              { label: "Settled", ts: row.settled_at },
                            ].filter((e) => !!e.ts);

                            if (events.length === 0)
                              return <span className="text-slate-400">—</span>;

                            return (
                              <ol className="relative">
                                {events.map(({ label, ts }, idx) => {
                                  const style =
                                    STAGE_STYLES[label] ||
                                    STAGE_STYLES.Suggested;
                                  const isLast = idx === events.length - 1;
                                  return (
                                    <li
                                      key={label}
                                      className="relative pl-5 pb-2.5 last:pb-0"
                                    >
                                      {!isLast && (
                                        <span
                                          aria-hidden
                                          className="absolute left-[5px] top-2 bottom-[-2px] w-px bg-slate-200"
                                        />
                                      )}
                                      <span
                                        aria-hidden
                                        className={`absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${style.dot}`}
                                      />
                                      <div
                                        className="leading-tight"
                                        title={new Date(ts).toLocaleString(
                                          "en-US",
                                          {
                                            timeZone: "America/Los_Angeles",
                                            dateStyle: "long",
                                            timeStyle: "long",
                                          },
                                        )}
                                      >
                                        <div
                                          className={`text-[11px] font-semibold uppercase tracking-wide ${style.text}`}
                                        >
                                          <span className="mr-1" aria-hidden>
                                            {style.icon}
                                          </span>
                                          {label}
                                        </div>
                                        <div className="text-[10px] text-slate-500 whitespace-nowrap">
                                          {formatAbsolute(ts)}
                                        </div>
                                        <div className="text-[10px] text-slate-400">
                                          {formatRelative(ts)}
                                        </div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-800">
                          {lane}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-800 whitespace-nowrap">
                          {row.load_id ? (
                            (() => {
                              const sid =
                                row.load_snapshot?.sid ||
                                row.load_snapshot?.search_id ||
                                null;
                              const href = `${DISPATCH_BOARD_URL}/loads/${encodeURIComponent(
                                row.load_id,
                              )}${sid ? `?search_id=${encodeURIComponent(sid)}` : ""}`;
                              return (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={row.load_id}
                                  className="text-indigo-600 hover:text-indigo-800 hover:underline font-mono text-xs"
                                >
                                  {String(row.load_id).length > 10
                                    ? `${String(row.load_id).slice(0, 10)}…`
                                    : row.load_id}
                                </a>
                              );
                            })()
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-800 whitespace-nowrap">
                          {row.chr_shipment_summary_line ? (
                            <div>
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 mb-0.5">
                                CHR confirmed
                              </div>
                              <div>{row.chr_shipment_summary_line}</div>
                            </div>
                          ) : (
                            row.load_snapshot?.sd ||
                            row.load_snapshot?.ship_date || (
                              <span className="text-slate-400">—</span>
                            )
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-800">
                          {posted}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-800">
                          {negotiated}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {hasRateCon ? (
                            <button
                              type="button"
                              onClick={() => viewRateCon(row, rateConCount - 1)}
                              className="!shadow-none !bg-transparent !text-emerald-700 hover:!underline !text-xs !font-semibold !border-0 !p-0"
                            >
                              ✓ View
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">
                              — Missing
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold text-slate-900 whitespace-nowrap">
                          {feeDisplay}
                        </td>
                        <td className="px-4 py-4 text-right whitespace-nowrap">
                          {(() => {
                            const snap = row.load_snapshot || {};
                            const isParade = snap.data_source === "parade";
                            const allowedActions = (
                              snap.allowed_actions || []
                            ).map((a) => String(a).toLowerCase());
                            const canQuote =
                              isParade &&
                              (!allowedActions.length ||
                                allowedActions.includes("quote"));
                            const canBook =
                              isParade &&
                              (!allowedActions.length ||
                                allowedActions.includes("book"));
                            if (!isParade) return null;
                            return (
                              <div className="flex items-center justify-end gap-1.5 mb-2">
                                {canQuote && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setParadeTarget({ row, mode: "quote" })
                                    }
                                    className="!shadow-none !bg-white !text-orange-700 !border !border-orange-300 hover:!bg-orange-50 !rounded-md !px-2 !py-1 !text-[11px] !font-semibold whitespace-nowrap"
                                  >
                                    Quote
                                  </button>
                                )}
                                {canBook && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setParadeTarget({ row, mode: "book" })
                                    }
                                    className="!shadow-none !bg-orange-600 hover:!bg-orange-700 !text-white !border-0 !rounded-md !px-2 !py-1 !text-[11px] !font-semibold whitespace-nowrap"
                                  >
                                    Book
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                          {tab === "pending" ? (
                            <div className="inline-flex flex-col items-stretch gap-2 min-w-[180px]">
                              {row.chr_ops_attention ===
                                "needs_intervention" && (
                                <>
                                  {row.chr_recommended_action && (
                                    <div
                                      className="text-[11px] text-rose-800 leading-snug text-right max-w-[220px] ml-auto"
                                      title={row.chr_last_error_detail || ""}
                                    >
                                      {row.chr_recommended_action}
                                    </div>
                                  )}
                                  <div className="inline-flex items-center gap-2 justify-end">
                                    <button
                                      type="button"
                                      onClick={() => retryChrSubmit(row)}
                                      disabled={
                                        busyAction ===
                                        `retry:${row.booking_request_id}`
                                      }
                                      className="!shadow-none !bg-white !text-rose-800 !border !border-rose-300 hover:!bg-rose-50 !rounded-lg !px-3 !py-2 !text-xs !font-semibold whitespace-nowrap flex-1"
                                    >
                                      {busyAction ===
                                      `retry:${row.booking_request_id}`
                                        ? "Retrying…"
                                        : "Retry CHR"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => resetBookItLink(row)}
                                      disabled={
                                        busyAction ===
                                        `reset:${row.booking_request_id}`
                                      }
                                      className="!shadow-none !bg-white !text-slate-700 !border !border-slate-300 hover:!bg-slate-50 !rounded-lg !px-3 !py-2 !text-xs !font-semibold whitespace-nowrap flex-1"
                                    >
                                      {busyAction ===
                                      `reset:${row.booking_request_id}`
                                        ? "Resetting…"
                                        : "Reset link"}
                                    </button>
                                  </div>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  canCharge && setChargeTarget(row)
                                }
                                disabled={!canCharge}
                                title={
                                  !hasRateCon
                                    ? "Upload rate con first"
                                    : !hasNegotiated
                                      ? "Enter negotiated rate first"
                                      : "Charge the carrier"
                                }
                                className={`!shadow-none !rounded-lg !px-3 !py-2 !text-xs !font-semibold whitespace-nowrap ${
                                  canCharge
                                    ? "!bg-indigo-600 hover:!bg-indigo-700 !text-white !border-0"
                                    : "!bg-slate-200 !text-slate-400 !border-0 !cursor-not-allowed"
                                }`}
                              >
                                {freeCredit
                                  ? "Book on free credit"
                                  : `Charge ${feeDisplay} & Book`}
                              </button>
                              <div className="inline-flex items-center gap-2 justify-end">
                                <button
                                  type="button"
                                  onClick={() => setUploadTarget(row)}
                                  className="!shadow-none !bg-indigo-50 !text-indigo-700 !border !border-indigo-200 hover:!bg-indigo-100 hover:!border-indigo-400 !rounded-lg !px-3 !py-2 !text-xs !font-semibold whitespace-nowrap flex-1"
                                >
                                  {hasRateCon ? "Edit rate" : "Upload"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFailTarget(row)}
                                  className="!shadow-none !bg-rose-50 !text-rose-700 !border !border-rose-200 hover:!bg-rose-100 hover:!border-rose-400 !rounded-lg !px-3 !py-2 !text-xs !font-semibold whitespace-nowrap flex-1"
                                >
                                  Couldn't book
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-3 text-xs">
                              {row.stripe_dashboard_url && (
                                <a
                                  href={row.stripe_dashboard_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:underline font-semibold"
                                >
                                  View in Stripe ↗
                                </a>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {chargeTarget && (
        <ChargeConfirmModal
          row={chargeTarget}
          onCancel={() => setChargeTarget(null)}
          onConfirmed={() => {
            setChargeTarget(null);
            refresh();
          }}
        />
      )}
      {failTarget && (
        <CouldNotBookModal
          row={failTarget}
          onCancel={() => setFailTarget(null)}
          onConfirmed={() => {
            setFailTarget(null);
            refresh();
          }}
        />
      )}
      {uploadTarget && (
        <RateConUploadModal
          row={uploadTarget}
          feeTiersText={feeTiersText}
          onCancel={() => setUploadTarget(null)}
          onConfirmed={() => {
            setUploadTarget(null);
            refresh();
          }}
        />
      )}
      {paradeTarget && (
        <ParadeTransactModal
          row={paradeTarget.row}
          mode={paradeTarget.mode}
          onClose={() => setParadeTarget(null)}
        />
      )}
    </DashboardLayout>
  );
}

function formatLane(snap) {
  if (!snap) return "—";
  const o = `${snap.oc || ""}, ${snap.os || ""}`.replace(/^,\s*|,\s*$/g, "");
  const d = `${snap.dc || ""}, ${snap.ds || ""}`.replace(/^,\s*|,\s*$/g, "");
  if (!o && !d) return "—";
  return `${o || "?"} → ${d || "?"}`;
}

function formatRateCents(cents, fallback = "—") {
  if (cents == null || cents === 0) return fallback;
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

function formatMethod(row) {
  if (!row.payment_method_type) return "—";
  const last4 = row.payment_method_last4 || "••••";
  if (row.payment_method_type === "ach") {
    return `${row.payment_method_bank_name || "Bank"} •• ${last4}`;
  }
  return `Card •• ${last4}`;
}

function formatAbsolute(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatRelative(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr}y ago`;
}
